import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useBoolean, useMount } from 'ahooks';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, IconButton, InputAdornment, Typography } from '@mui/material';
import { FormText } from '@/components/form/form-text';
import { StepBox } from '@/components/layout/step-box';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useFlow } from '@/hooks/use-flow';
import { useLidForm } from '@/hooks/use-lid-form';
import { useRules } from '@/hooks/use-rules';
import { AppCoreContext } from '@/providers/core-provider';

interface PasswordValues {
  password: string;
}

export const LogoutPage = () => {
  const { t } = useTranslation();
  const { setFlow } = useFlow();
  const rules = useRules();
  const [userName, setUserName] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const { setBizState } = useBizState();
  const [urlParams] = useSearchParams();
  const { lenovoId } = useContext(AppCoreContext);
  const [showPassword, { toggle }] = useBoolean(false);
  const callback = urlParams.get('post_logout_redirect_uri') || '';
  const state = urlParams.get('state') || '';
  const { control, setError, handleSubmit } = useLidForm<PasswordValues>({
    defaultValues: {
      password: '',
    },
  });

  const account = areaCode ? `${areaCode} ${userName}` : userName;

  const { run } = useCoreRequest(() => lenovoId.openAppInit(), {
    manual: true,
    onSuccess: ({ data }) => {
      if ('userName' in data) {
        setUserName(data.userName);
      }
      if ('areaCode' in data) {
        setAreaCode(data.areaCode);
      }
    },
  });

  const { run: runSubmit, loading: submitting } = useCoreRequest(
    (params: PasswordValues) => {
      return lenovoId.logoutVerifyPassword({
        userName,
        areaCode,
        password: params.password,
        state,
        redirectUrl: callback,
        et: '2',
        bid: window.bid,
      });
    },
    {
      manual: true,
      onSuccess: ({ data, nextSteps }) => {
        if (nextSteps === Flow.LOGOUT_STEP_SUCCESS_CALLBACK) {
          if (data.callBack) {
            window.location.href = data.callBack;
            return;
          }
        }
      },
      onError: (code) => {
        switch (code) {
          case ErrorCode.InvalidPasswordFormat:
            setError('password', t('password_format_error'));
            return true;
          case ErrorCode.InvalidUsernameFormat:
          case ErrorCode.UsernameNotExists:
            setError('password', t('password_incorrect_error'));
            return true;
          case ErrorCode.LoginLocked:
            setBizState({ signInBlocked: true });
            return true;
          case ErrorCode.UserFrozen:
            setFlow(Flow.LOGIN_STEP_DISABLE_RECOVERY);
            return true;
          case ErrorCode.UserDisabled:
            setFlow(Flow.LOGIN_STEP_DISABLE);
            return true;
        }
      },
    },
  );

  const submit = handleSubmit((data) => {
    runSubmit(data);
  });

  useMount(() => {
    run();
  });

  return (
    <StepBox title={t('two_factor_auth_title')}>
      <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
        {t('for_your_account_security', {
          account: account,
        })}
      </Typography>

      <form onSubmit={submit} noValidate>
        <FormText
          name="password"
          label={t('password', { defaultValue: 'password' })}
          control={control}
          rules={rules.password}
          type={showPassword ? 'text' : 'password'}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggle} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Button type="submit" fullWidth variant="contained" loading={submitting} sx={{ my: 2 }}>
          {t('continue')}
        </Button>
      </form>
    </StepBox>
  );
};
